import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import { format } from "date-fns";
import NotoSans from "assets/Fonts/arial-unicode-ms.ttf";

Font.register({
  family: "Noto Sans",
  fonts: [{ src: NotoSans }],
});

// Colors - Corporate Palette
const COLORS = {
  primary: "#3B0069",
  secondary: "#7C3AED",
  text: "#1F2937",
  muted: "#6B7280",
  light: "#F3F4F6",
  white: "#FFFFFF",
  success: "#10B981",
  successBg: "#ECFDF5",
  warning: "#F59E0B",
  warningBg: "#FFFBEB",
  danger: "#EF4444",
  dangerBg: "#FEF2F2",
  border: "#E5E7EB",
  subHeader: "#F9FAFB",
};

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Noto Sans",
    color: COLORS.text,
    backgroundColor: COLORS.white,
    fontSize: 9,
  },
  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: COLORS.primary,
    paddingBottom: 10,
  },
  brandTitle: {
    fontSize: 22,
    fontWeight: "bold",
    color: COLORS.primary,
    textTransform: "uppercase",
  },
  reportMeta: {
    textAlign: "right",
  },
  metaText: {
    fontSize: 8,
    color: COLORS.muted,
    marginBottom: 2,
  },
  orgName: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.text,
    marginBottom: 4,
  },
  // Sections
  section: {
    marginBottom: 20,
    break: false, 
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.light,
    padding: 8,
    borderRadius: 4,
    marginBottom: 10,
    borderLeftWidth: 4,
    borderLeftColor: COLORS.primary,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.primary,
    textTransform: "uppercase",
  },
  // Stats
  statsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 20,
  },
  statCard: {
    flex: 1,
    minWidth: "30%",
    padding: 10,
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 6,
    alignItems: "center",
  },
  statValue: {
    fontSize: 18,
    fontWeight: "heavy",
    color: COLORS.primary,
  },
  statLabel: {
    fontSize: 7,
    color: COLORS.muted,
    textTransform: "uppercase",
    marginTop: 2,
  },
  // Cards (for detailed items like WO, PO)
  card: {
    marginBottom: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 6,
    overflow: "hidden",
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: COLORS.subHeader,
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    alignItems: "center",
  },
  cardTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: COLORS.text,
  },
  cardSubTitle: {
    fontSize: 8,
    color: COLORS.muted,
  },
  cardBody: {
    padding: 8,
  },
  // Tables
  table: {
    width: "100%",
    marginBottom: 5,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#EFF6FF", // Light Blueish
    borderBottomWidth: 1,
    borderBottomColor: COLORS.secondary,
    paddingVertical: 4,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingVertical: 4,
    alignItems: "flex-start", // align top for multi-line text
  },
  th: {
    color: COLORS.primary,
    fontWeight: "bold",
    fontSize: 8,
    paddingHorizontal: 4,
  },
  td: {
    fontSize: 8,
    color: COLORS.text,
    paddingHorizontal: 4,
  },
  // Nested Sub-Tables (Scopes, Materials)
  subTableContainer: {
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    paddingTop: 8,
  },
  subTableTitle: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.muted,
    marginBottom: 4,
    textTransform: "uppercase",
  },
  subRow: {
    flexDirection: "row",
    paddingVertical: 2,
  },
  // Badges
  badge: {
     paddingHorizontal: 6,
     paddingVertical: 2,
     borderRadius: 4,
     fontSize: 7,
     fontWeight: "bold",
     textTransform: "uppercase",
  },
  // Footer
  footer: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    paddingTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    color: COLORS.muted,
    fontSize: 7,
  },
  // Utils
  bold: { fontWeight: "bold" },
  right: { textAlign: "right" },
  center: { textAlign: "center" },
  w10: { width: "10%" },
  w15: { width: "15%" },
  w20: { width: "20%" },
  w25: { width: "25%" },
  w30: { width: "30%" },
  w40: { width: "40%" },
  w50: { width: "50%" },
  w60: { width: "60%" },
  row: { flexDirection: "row", justifyContent: "space-between", marginBottom: 3 },
});

const GlobalPDFReport = ({ data }) => {
  if (!data) return null;
  const { organization, date_range, section_counts, sections } = data;

  // --- Helpers ---
  const formatCurrency = (amount) => {
    const value = Number(amount);
    if (!Number.isFinite(value)) return "-";
    return `Rs ${value.toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd MMM yyyy");
    } catch {
      return dateString;
    }
  };

  const StatusBadge = ({ status }) => {
    const s = status?.toString().toLowerCase();
    let bg = COLORS.light;
    let color = COLORS.muted;

    if (["completed", "active", "approved", "paid", "ordered"].includes(s)) {
      bg = COLORS.successBg;
      color = COLORS.success;
    } else if (["pending", "open", "draft", "partially_paid"].includes(s)) {
      bg = COLORS.warningBg;
      color = COLORS.warning;
    } else if (["cancelled", "closed", "return", "unpaid"].includes(s)) {
      bg = COLORS.dangerBg;
      color = COLORS.danger;
    }

    return (
      <View style={[styles.badge, { backgroundColor: bg }]}>
        <Text style={{ color: color }}>{status}</Text>
      </View>
    );
  };

  // --- Header ---
  const ReportHeader = () => (
    <View style={styles.header}>
      <View>
        <Text style={styles.brandTitle}>Global Report</Text>
        <Text style={styles.orgName}>{organization?.name}</Text>
      </View>
      <View style={styles.reportMeta}>
        <Text style={styles.metaText}>Generated On: {format(new Date(), "PPpp")}</Text>
        <Text style={styles.metaText}>
          Period: {formatDate(date_range?.start_date)} - {formatDate(date_range?.end_date)}
        </Text>
      </View>
    </View>
  );

  // --- Footer ---
  const ReportFooter = () => (
    <View style={styles.footer} fixed>
      <Text style={styles.footerText}>Generated by SiteMaster AI</Text>
      <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
    </View>
  );

  // --- Stats Section ---
  const ExecutiveSummary = () => (
    <View style={styles.section} wrap={false}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Executive Summary</Text>
      </View>
      <View style={styles.statsContainer}>
        {Object.entries(section_counts).map(([key, value]) => (
          <View key={key} style={styles.statCard}>
            <Text style={styles.statValue}>{value}</Text>
            <Text style={styles.statLabel}>{key.replace(/_/g, " ")}</Text>
          </View>
        ))}
      </View>
    </View>
  );

  // --- Section Renderers --- 

  const RenderMaterialRequests = () => {
    if (!sections.material_request?.length) return null;
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Material Requests</Text>
        </View>
        {sections.material_request.map((mr) => (
          <View key={mr.id} style={styles.card} wrap={false}>
            <View style={styles.cardHeader}>
              <View>
                <Text style={styles.cardTitle}>{mr.mr_number} • {mr.site_name}</Text>
                <Text style={styles.cardSubTitle}>Created by {mr.created_by} on {formatDate(mr.created_on)}</Text>
              </View>
              <StatusBadge status={mr.status} />
            </View>
            <View style={styles.cardBody}>
               {/* Material Lines Table */}
               {mr.material_lines?.length > 0 && (
                <View>
                  <View style={styles.tableHeader}>
                    <Text style={[styles.th, styles.w40]}>Material</Text>
                    <Text style={[styles.th, styles.w20, styles.right]}>Qty</Text>
                    <Text style={[styles.th, styles.w20]}>UOM</Text>
                    <Text style={[styles.th, styles.w20]}>Status</Text>
                  </View>
                  {mr.material_lines.map((line, idx) => (
                    <View key={idx} style={styles.tableRow}>
                      <Text style={[styles.td, styles.w40]}>{line.material_name}</Text>
                      <Text style={[styles.td, styles.w20, styles.right]}>{line.quantity}</Text>
                      <Text style={[styles.td, styles.w20]}>{line.uom}</Text>
                      <Text style={[styles.td, styles.w20]}>{line.status}</Text>
                    </View>
                  ))}
                </View>
               )}
            </View>
          </View>
        ))}
      </View>
    );
  };

  const RenderPurchaseOrders = () => {
    if (!sections.po?.length) return null;
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Purchase Orders</Text>
        </View>
        {sections.po.map((po) => (
          <View key={po.id} style={styles.card} wrap={false}>
            <View style={styles.cardHeader}>
               <View>
                 <Text style={styles.cardTitle}>{po.po_number} • {po.vendor_name}</Text>
                 <Text style={styles.cardSubTitle}>Date: {po.order_date}</Text>
               </View>
               <View style={{alignItems: 'flex-end'}}>
                 <StatusBadge status={po.status} />
                 <Text style={{fontSize: 9, fontWeight: 'bold', marginTop: 2}}>{formatCurrency(po.total_price)}</Text>
               </View>
            </View>
            <View style={styles.cardBody}>
              <View style={[styles.row, {marginBottom: 8}]}>
                 <Text style={styles.td}><Text style={styles.bold}>Site:</Text> {po.site_name || "N/A"}</Text>
                 <Text style={styles.td}><Text style={styles.bold}>Paid:</Text> {formatCurrency(po.paid_amount)}</Text>
                 <Text style={styles.td}><Text style={styles.bold}>Balance:</Text> {formatCurrency(po.remaining_payment)}</Text>
              </View>

              {/* Material Lines with Pricing */}
              {po.material_lines?.length > 0 && (
                 <View style={styles.subTableContainer}>
                    <View style={styles.tableHeader}>
                      <Text style={[styles.th, styles.w40]}>Item</Text>
                      <Text style={[styles.th, styles.w15, styles.right]}>Qty</Text>
                      <Text style={[styles.th, styles.w15, styles.right]}>Rate</Text>
                      <Text style={[styles.th, styles.w30, styles.right]}>Total</Text>
                    </View>
                    {po.material_lines.map((line, idx) => (
                      <View key={idx} style={styles.tableRow}>
                        <Text style={[styles.td, styles.w40]}>{line.material_name}</Text>
                        <Text style={[styles.td, styles.w15, styles.right]}>{line.quantity} {line.uom}</Text>
                        <Text style={[styles.td, styles.w15, styles.right]}>{formatCurrency(line.unit_price)}</Text>
                        <Text style={[styles.td, styles.w30, styles.right]}>{formatCurrency(line.total_price)}</Text>
                      </View>
                    ))}
                 </View>
              )}
            </View>
          </View>
        ))}
      </View>
    );
  };

  const RenderWorkOrders = () => {
    if (!sections.work_orders?.length) return null;
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Work Orders</Text>
        </View>
        {sections.work_orders.map((wo) => (
          <View key={wo.id} style={styles.card} wrap={false}>
             <View style={styles.cardHeader}>
               <View>
                 <Text style={styles.cardTitle}>{wo.contractor_name} • {wo.site_name}</Text>
                 <Text style={styles.cardSubTitle}>Issued: {formatDate(wo.date_of_issue)} • Ver: {wo.current_details?.version_number}</Text>
               </View>
               <View style={{alignItems: 'flex-end'}}>
                 <StatusBadge status={wo.status} />
                 <Text style={{fontSize: 9, fontWeight: 'bold', marginTop: 2}}>Value: {formatCurrency(wo.total_value)}</Text>
               </View>
             </View>
             
             <View style={styles.cardBody}>
                {/* Contract Details */}
                <View style={[styles.row, { flexWrap: 'wrap' }]}>
                  <Text style={[styles.td, {width: '33%'}]}><Text style={styles.bold}>Type:</Text> {wo.current_details?.contract_type}</Text>
                  <Text style={[styles.td, {width: '33%'}]}><Text style={styles.bold}>Structure:</Text> {wo.current_details?.payment_structure?.replace(/_/g, " ")}</Text>
                  <Text style={[styles.td, {width: '33%'}]}><Text style={styles.bold}>Start:</Text> {formatDate(wo.current_details?.start_date)}</Text>
                </View>
                
                {/* Scope Items */}
                {wo.current_details?.scope_items?.length > 0 && (
                  <View style={styles.subTableContainer}>
                    <Text style={styles.subTableTitle}>Scope of Work</Text>
                    <View style={styles.tableHeader}>
                      <Text style={[styles.th, styles.w40]}>Item</Text>
                      <Text style={[styles.th, styles.w20]}>Desc</Text>
                      <Text style={[styles.th, styles.w20, styles.right]}>Qty/Rate</Text>
                      <Text style={[styles.th, styles.w20, styles.right]}>Total</Text>
                    </View>
                    {wo.current_details.scope_items.map((item, idx) => (
                      <View key={idx} style={styles.tableRow}>
                        <Text style={[styles.td, styles.w40]}>{item.item}</Text>
                        <Text style={[styles.td, styles.w20]}>{item.description || "-"}</Text>
                        <Text style={[styles.td, styles.w20, styles.right]}>{item.quantity} x {formatCurrency(item.unit_price)}</Text>
                        <Text style={[styles.td, styles.w20, styles.right]}>{formatCurrency(item.total_price)}</Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* Milestones */}
                {wo.current_details?.milestones?.length > 0 && (
                  <View style={styles.subTableContainer}>
                    <Text style={styles.subTableTitle}>Milestones</Text>
                    <View style={styles.tableHeader}>
                      <Text style={[styles.th, styles.w40]}>Name</Text>
                      <Text style={[styles.th, styles.w30]}>Amount</Text>
                      <Text style={[styles.th, styles.w30]}>Paid</Text>
                    </View>
                    {wo.current_details.milestones.map((m, idx) => (
                      <View key={idx} style={styles.tableRow}>
                        <Text style={[styles.td, styles.w40]}>{m.name}</Text>
                        <Text style={[styles.td, styles.w30]}>{formatCurrency(m.amount)}</Text>
                        <Text style={[styles.td, styles.w30]}>{formatCurrency(m.paid_amount)}</Text>
                      </View>
                    ))}
                  </View>
                )}
             </View>
          </View>
        ))}
      </View>
    );
  };

  const RenderRFQs = () => {
    if (!sections.rfq?.length) return null;
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Request for Quotations</Text>
        </View>
        {sections.rfq.map((rfq) => (
           <View key={rfq.id} style={styles.card} wrap={false}>
              <View style={styles.cardHeader}>
                 <View>
                   <Text style={styles.cardTitle}>{rfq.rfq_number}</Text>
                   <Text style={styles.cardSubTitle}>{rfq.site_name || "Warehouse Request"}</Text>
                 </View>
                 <StatusBadge status={rfq.status} />
              </View>
              {rfq.material_lines?.length > 0 && (
                <View style={styles.cardBody}>
                  <View style={styles.tableHeader}>
                     <Text style={[styles.th, styles.w60]}>Material</Text>
                     <Text style={[styles.th, styles.w40, styles.right]}>Quantity</Text>
                  </View>
                  {rfq.material_lines.map((line, idx) => (
                    <View key={idx} style={styles.tableRow}>
                      <Text style={[styles.td, styles.w60]}>{line.material_name}</Text>
                      <Text style={[styles.td, styles.w40, styles.right]}>{line.quantity} {line.uom}</Text>
                    </View>
                  ))}
                </View>
              )}
           </View>
        ))}
      </View>
    );
  };

  const RenderDesigns = () => {
    if (!sections.design?.length) return null;
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Design Log</Text>
        </View>
        <View style={styles.table}>
           <View style={styles.tableHeader}>
              <Text style={[styles.th, styles.w40]}>Design Name</Text>
              <Text style={[styles.th, styles.w20]}>Type</Text>
              <Text style={[styles.th, styles.w20]}>Site</Text>
              <Text style={[styles.th, styles.w20]}>Status</Text>
           </View>
           {sections.design.map((d, idx) => (
             <View key={d.id} style={styles.tableRow}>
               <Text style={[styles.td, styles.w40]}>{d.design_name}</Text>
               <Text style={[styles.td, styles.w20]}>{d.type}</Text>
               <Text style={[styles.td, styles.w20]}>{d.site_name}</Text>
               <Text style={[styles.td, styles.w20]}>{d.status}</Text>
             </View>
           ))}
        </View>
      </View>
    );
  };

  const RenderContractorPayments = () => {
    if (!sections.contractor_workorder_payment?.length) return null;
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Contractor Payments</Text>
        </View>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.th, styles.w25]}>Contractor</Text>
            <Text style={[styles.th, styles.w20]}>Date</Text>
            <Text style={[styles.th, styles.w20]}>Amount</Text>
            <Text style={[styles.th, styles.w15]}>Mode</Text>
            <Text style={[styles.th, styles.w20]}>Remarks</Text>
          </View>
          {sections.contractor_workorder_payment.map((pay, idx) => (
            <View key={pay.id} style={styles.tableRow}>
              <Text style={[styles.td, styles.w25]}>{pay.contractor_name}</Text>
              <Text style={[styles.td, styles.w20]}>{formatDate(pay.created_on)}</Text>
              <Text style={[styles.td, styles.w20]}>{formatCurrency(pay.amount)}</Text>
              <Text style={[styles.td, styles.w15]}>{pay.payment_mode}</Text>
              <Text style={[styles.td, styles.w20]}>{pay.remarks || "-"}</Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const RenderVendorPayments = () => {
    if (!sections.vendor_advance_payment?.length) return null;
    return (
       <View style={styles.section}>
         <View style={styles.sectionHeader}>
           <Text style={styles.sectionTitle}>Vendor Advance Payments</Text>
         </View>
         <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.th, styles.w25]}>Vendor</Text>
              <Text style={[styles.th, styles.w20]}>Date</Text>
              <Text style={[styles.th, styles.w20]}>Amount</Text>
              <Text style={[styles.th, styles.w15]}>Mode</Text>
              <Text style={[styles.th, styles.w20]}>Remarks</Text>
            </View>
            {sections.vendor_advance_payment.map((pay, idx) => (
              <View key={pay.id} style={styles.tableRow}>
                <Text style={[styles.td, styles.w25]}>{pay.vendor_name}</Text>
                <Text style={[styles.td, styles.w20]}>{pay.payment_date}</Text>
                <Text style={[styles.td, styles.w20]}>{formatCurrency(pay.amount)}</Text>
                <Text style={[styles.td, styles.w15]}>{pay.payment_method}</Text>
                <Text style={[styles.td, styles.w20]}>{pay.remarks || "-"}</Text>
              </View>
            ))}
         </View>
       </View>
    );
  };

  return (
    <Document title={`SiteMaster Global Report - ${organization?.name}`}>
      <Page size="A4" style={styles.page}>
        <ReportHeader />
        <ExecutiveSummary />
        <RenderWorkOrders />
        <RenderPurchaseOrders />
        <RenderMaterialRequests />
        <RenderRFQs />
        <RenderContractorPayments />
        <RenderVendorPayments />
        <RenderDesigns />
        <ReportFooter />
      </Page>
    </Document>
  );
};

export default GlobalPDFReport;
